import React, {ReactElement, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useAddElementAction} from '../../../../context/AddElementsContext';
import {useSlideErrors} from '../../../../context/SlideErrorsContext';
import {useHotkeysSlideElementKeys} from '../../../../hooks/useHotkeysSlideElementKeys';
import {IElement} from '../../../../incase-domain/elements';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import ElementComponent from '../../../elements/element';
import CenteredGridLines from '../../highlighted/common/CenteredGridLines';

interface ElementsDesktopProps {
    slideSize: {width: number; height: number};
    slideId: string;
    elements: IElement[];
    cellWidth: number;
    cellHeight: number;
    onOptions?: (id: string, silent?: boolean) => void;
}

function ElementsDesktop({slideSize, slideId, elements, cellWidth, cellHeight, onOptions}: ElementsDesktopProps): ReactElement {
    const slide = useSelector(selectedSlideSelector);

    const {errors} = useSlideErrors();
    const addElement = useAddElementAction();

    const generatePoints = useMemo(() => {
        const points: {[key: string]: {x: number; y: number}[]} = {};
        elements.forEach((el, i) => {
            points[i] = [
                {x: el.xPosition, y: el.yPosition},
                {x: el.xPosition + +el.width.slice(0, -1) / 2, y: el.yPosition + +el.height.slice(0, -1) / 2},
                {x: el.xPosition + +el.width.slice(0, -1), y: +(el.yPosition + +el.height.slice(0, -1))},
            ];
        });
        return points;
    }, [elements]);

    useHotkeysSlideElementKeys((type) => addElement(type));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: `${slide.gapBetweenMobileElements}em`,
                paddingTop: `${slide.elementPadding?.top}em`,
                paddingRight: `${slide.elementPadding?.right}em`,
                paddingBottom: `${slide.elementPadding?.bottom}em`,
                paddingLeft: `${slide.elementPadding?.left}em`,
            }}
        >
            <CenteredGridLines slideSize={slideSize} />

            {elements.map((element, index) => {
                const keyIndex = `slide-${slideId}-element-${element.id || index}-inner`;
                const slideErrors = errors ? errors.errors : {};
                const hasErrors =
                    (slideErrors?.elements &&
                        Array.isArray(slideErrors.elements) &&
                        slideErrors.elements[0] &&
                        Object.keys(slideErrors.elements[0]).includes(element.id)) ||
                    undefined;

                return (
                    <div key={keyIndex} style={{touchAction: 'none'}}>
                        <ElementComponent
                            element={{
                                ...element,
                                elementIndex: index,
                                keyIndex,
                            }}
                            clientWidth={slideSize.width}
                            clientHeight={slideSize.height}
                            cellWidth={cellWidth}
                            cellHeight={cellHeight}
                            onOptions={onOptions}
                            assistPoints={generatePoints}
                            index={index}
                            hasErrors={hasErrors}
                        />
                    </div>
                );
            })}
        </div>
    );
}

export default ElementsDesktop;
